import { useParams, Link, useNavigate } from "react-router-dom";
import { useLanguage } from "@/context/LanguageContext";
import { Shield, Heart, Briefcase, Globe, Activity, FileText, ArrowLeft, ArrowRight, Fuel, BarChart3, Truck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import FloatingButtons from "@/components/FloatingButtons";
import BookingModal from "@/components/BookingModal";
import SEOHead from "@/components/SEOHead";
import { useState, useEffect } from "react";
import { Toaster } from "sonner";

const ICON_MAP = { Shield, Heart, Briefcase, Globe, Activity, FileText, Fuel, BarChart3, Truck };

export default function ServicePage() {
  const { serviceId } = useParams();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [modalOpen, setModalOpen] = useState(false);
  const [preselectedCaseType, setPreselectedCaseType] = useState("");

  const areas = t("practiceAreas.items") || [];
  const attorneys = t("attorneys.items") || [];
  const sp = t("servicePage");
  const area = areas.find((a) => a.id === serviceId);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [serviceId]);

  if (!area) {
    return (
      <div className="min-h-screen bg-[#f5f0e8] flex items-center justify-center">
        <div className="text-center">
          <h1 className="font-heading text-3xl font-extrabold text-[#1a1a2e] mb-4">Service Not Found</h1>
          <Link to="/" className="text-[#1a5c4a] font-semibold hover:underline">{sp.backHome}</Link>
        </div>
      </div>
    );
  }

  const Icon = ICON_MAP[area.icon];
  const attorney = attorneys.find((a) => a.name === area.attorney);
  const relatedAreas = areas.filter((a) => a.id !== serviceId).slice(0, 3);

  const openBooking = (caseType = "") => {
    setPreselectedCaseType(caseType);
    setModalOpen(true);
  };

  return (
    <div className="min-h-screen bg-[#f5f0e8]" data-testid="service-page">
      <SEOHead
        title={`${area.title} — Legal Services`}
        description={area.shortDesc}
      />
      <Toaster position="top-center" richColors />
      <Header onBookingOpen={() => openBooking(area.title)} />

      {/* Hero */}
      <section className="pt-28 pb-16 md:pt-36 md:pb-24 bg-[#1a1a2e] relative overflow-hidden" data-testid="service-hero">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-[#1a5c4a] rounded-full blur-[120px]" />
        </div>
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 relative z-10">
          <Link
            to="/"
            data-testid="back-home-link"
            className="inline-flex items-center gap-2 text-white/60 hover:text-white text-sm mb-8 transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            {sp.backHome}
          </Link>

          <div className="flex items-start gap-5 mb-6">
            <div className="w-14 h-14 rounded-2xl bg-[#1a5c4a] flex items-center justify-center shrink-0">
              {Icon && <Icon className="h-6 w-6 text-white" />}
            </div>
            <div>
              <h1
                data-testid="service-title"
                className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white leading-[1.1] tracking-[-0.02em]"
              >
                {area.title}
              </h1>
              <p className="text-white/60 mt-3 max-w-2xl text-base md:text-lg leading-relaxed">
                {area.shortDesc}
              </p>
            </div>
          </div>

          <Button
            data-testid="service-hero-cta"
            onClick={() => openBooking(area.title)}
            className="bg-[#1a5c4a] text-white hover:bg-[#154a3c] rounded-full px-8 py-6 text-sm font-semibold mt-4"
          >
            {sp.scheduleCta} <ArrowRight className="h-4 w-4 ml-2" />
          </Button>
        </div>
      </section>

      {/* Full Description */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <div className="grid lg:grid-cols-3 gap-12 lg:gap-16">
            <div className="lg:col-span-2">
              <p className="text-[#4a4540] text-base md:text-lg leading-relaxed">{area.fullDesc}</p>

              {/* Cases We Handle */}
              <div className="mt-12" data-testid="service-cases">
                <h2 className="font-heading text-2xl font-bold text-[#1a1a2e] mb-6">{sp.casesTitle}</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {area.cases.map((c) => (
                    <div key={c} className="flex items-center gap-3 p-4 bg-[#faf8f4] rounded-xl">
                      <div className="w-2 h-2 rounded-full bg-[#1a5c4a] shrink-0" />
                      <span className="text-[#1a1a2e] text-sm font-medium">{c}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* FAQ */}
              <div className="mt-12" data-testid="service-faq">
                <h2 className="font-heading text-2xl font-bold text-[#1a1a2e] mb-6">{sp.faqTitle}</h2>
                <Accordion type="single" collapsible className="w-full">
                  {area.faq.map((item, i) => (
                    <AccordionItem key={`${area.id}-faq-${i}`} value={`faq-${i}`} className="border-[#e0d9ce]">
                      <AccordionTrigger className="text-left text-[#1a1a2e] hover:text-[#1a5c4a] hover:no-underline font-medium">
                        {item.q}
                      </AccordionTrigger>
                      <AccordionContent className="text-[#4a4540] leading-relaxed">
                        {item.a}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {attorney && (
                <div className="bg-[#faf8f4] rounded-2xl p-6" data-testid="service-attorney">
                  <p className="text-[#1a5c4a] text-xs uppercase tracking-[0.3em] mb-4 font-semibold">{sp.leadAttorney}</p>
                  <img
                    src={attorney.photo}
                    alt={attorney.name}
                    className="w-full h-56 object-cover rounded-xl mb-4"
                  />
                  <h3 className="font-heading text-lg font-bold text-[#1a1a2e]">{attorney.name}</h3>
                  <p className="text-sm text-[#4a4540]">{attorney.title}</p>
                  <p className="text-xs text-[#1a5c4a] mt-1 font-medium">{attorney.yearsExperience}+ {sp.yearsExp}</p>
                  <div className="flex flex-wrap gap-1.5 mt-3">
                    {attorney.awards.map((award) => (
                      <Badge key={award} variant="secondary" className="bg-[#e8e3db] text-[#1a1a2e] border-0 rounded-full px-3 py-1 text-xs">
                        {award}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              <Button
                data-testid="service-sidebar-cta"
                onClick={() => openBooking(area.title)}
                className="w-full bg-[#1a5c4a] text-white hover:bg-[#154a3c] rounded-full px-6 py-5 font-semibold"
              >
                {sp.scheduleCta} <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Related Services */}
      <section className="py-16 md:py-24 bg-[#f5f0e8]" data-testid="related-services">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <h2 className="font-heading text-2xl font-bold text-[#1a1a2e] mb-8">{sp.relatedServices}</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {relatedAreas.map((ra) => {
              const RaIcon = ICON_MAP[ra.icon];
              return (
                <Link
                  key={ra.id}
                  to={`/services/${ra.id}`}
                  data-testid={`related-service-${ra.id}`}
                  className="bg-white rounded-2xl p-6 hover:shadow-lg transition-all duration-300 hover:-translate-y-1 group"
                >
                  <div className="w-10 h-10 rounded-xl bg-[#1a1a2e] flex items-center justify-center mb-4 group-hover:bg-[#1a5c4a] transition-colors">
                    {RaIcon && <RaIcon className="h-5 w-5 text-white" />}
                  </div>
                  <h3 className="font-heading text-lg font-bold text-[#1a1a2e] mb-2">{ra.title}</h3>
                  <p className="text-[#4a4540] text-sm line-clamp-2">{ra.shortDesc}</p>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-[#1a1a2e]" data-testid="service-cta">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="font-heading text-3xl sm:text-4xl font-extrabold text-white mb-4 tracking-[-0.02em]">
            {sp.ctaHeadline.replace("{service}", area.title)}
          </h2>
          <p className="text-white/60 mb-8 leading-relaxed">{sp.ctaDesc}</p>
          <Button
            data-testid="service-bottom-cta"
            onClick={() => openBooking(area.title)}
            className="bg-[#1a5c4a] text-white hover:bg-[#154a3c] rounded-full px-10 py-6 text-sm font-semibold"
          >
            {sp.ctaBtn} <ArrowRight className="h-4 w-4 ml-2" />
          </Button>
        </div>
      </section>

      <Footer onBookingOpen={() => openBooking(area.title)} />
      <FloatingButtons />
      <BookingModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        preselectedCaseType={preselectedCaseType}
        preselectedAttorney=""
      />
    </div>
  );
}
